import { useQueryClient } from '@tanstack/react-query';
import { memo } from 'react';

import { type Form, type ShortUrlResponse } from '@air/api/types';
import { Info as InfoIcon } from '@air/next-icons';
import { Tooltip } from '@air/primitive-tooltip';
import { cn } from '@air/utils-tailwind';

import { ContentCollectionAutoSubmitToggle } from '~/components/Modals/ContentCollectionModal/ContentCollectionAutoSubmitToggle';
import { ContentCollectionRequireAccountToggle } from '~/components/Modals/ContentCollectionModal/ContentCollectionRequireAccountToggle';
import { type MenuAction } from '~/components/UI/CopyField';
import { getPublicBoardCustomFieldsKey } from '~/constants/react-query-keys';
import { useFormPermissions } from '~/hooks/useFormPermissions';
import { useShortUrlPermissions } from '~/hooks/useShortUrlPermissions';
import { type EditShareLinkView } from '~/types/ShareLink';
import { canCreateShortUrl, canDeleteShortUrl } from '~/utils/permissions/shortUrlPermissions';
import { ContentCollectionCopyLink } from './ContentCollectionCopyLink';
import { ContentCollectionCreateSubmissionBoardToggle } from './ContentCollectionCreateSubmissionBoardToggle';
import { CustomFieldsSection } from './CustomFieldsSection';
import { FormDescription } from './FormDescription';
import { FormTitle } from './FormTitle';
import { SettingsItem } from './SettingsItem';

//currently this modal is only ever opened from the share button in a board header
const trackLocation = 'header';

type ContentCollectionEditActionsProps = {
  form: Form;
  shortIdInfo: ShortUrlResponse;
  setView: (view: EditShareLinkView) => void;
};

export const ContentCollectionEditActions = memo(
  ({ form, shortIdInfo, setView }: ContentCollectionEditActionsProps) => {
    const queryClient = useQueryClient();
    const _fields = form.fields;
    const formFields = _fields?.filter((field) => field.type !== 'custom_field' && field.type !== 'title');
    const { formPermissions } = useFormPermissions({
      formId: form.id,
    });

    const { shortUrlPermissions } = useShortUrlPermissions({ shortId: shortIdInfo.id });

    const menuActions: MenuAction[] = [];

    if (canDeleteShortUrl(shortUrlPermissions)) {
      menuActions.push({
        text: 'Delete link',
        action: () => setView('delete'),
      });

      if (canCreateShortUrl(formPermissions)) {
        menuActions.push({
          text: 'Generate a new link',
          action: () => setView('regenerate'),
        });
      }
    }

    return (
      <>
        <div className="flex flex-col gap-2" data-testid="CONTENT_COLLECTION_FORM_DETAILS_EDIT">
          <div className="flex flex-col gap-2">
            <div className="font-medium text-14 text-grey-10">Form details</div>
            <FormTitle form={form} />
            <FormDescription form={form} />
          </div>

          <ContentCollectionAutoSubmitToggle form={form} />
        </div>
        <div className={cn('flex flex-col gap-2', form.autoSubmit && 'pointer-events-none opacity-40')}>
          <div className="flex items-center gap-1 text-grey-10">
            <div className="font-medium text-14 text-grey-10">Form fields</div>
            <Tooltip
              className="w-72"
              label="Add fields to your form so visitors can categorize their uploads. Require certain fields, or leave them optional."
            >
              <InfoIcon className="size-4" />
            </Tooltip>
          </div>
          <div className="flex flex-col gap-2">
            {formFields?.map((formField) => (
              <SettingsItem form={form} key={formField.id} formField={formField} />
            ))}
            <CustomFieldsSection
              form={form}
              fields={_fields}
              onCreate={() =>
                queryClient.invalidateQueries({ queryKey: getPublicBoardCustomFieldsKey(form.boardId, shortIdInfo.id) })
              }
              trackLocation={trackLocation}
            />
          </div>
        </div>

        <div className="h-px bg-grey-5" />
        <div className="flex flex-col gap-2">
          <ContentCollectionRequireAccountToggle boardId={form.boardId} formId={form.id} shortIdInfo={shortIdInfo} />
          <ContentCollectionCreateSubmissionBoardToggle form={form} />
        </div>

        <div className="flex flex-col gap-3">
          <ContentCollectionCopyLink shortIdInfo={shortIdInfo} menuActions={menuActions} />
        </div>
      </>
    );
  },
);

ContentCollectionEditActions.displayName = 'ContentCollectionEditActions';
